import { useParams } from 'react-router';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from '@/react-intl';
import BaseApi from '@/uptk/core/api/BaseApi';
import { Button } from '@/design-system/ds-0/components/Button';
import type { StudioCommand } from './commandCatalog';

type Preparation = { available: boolean; approvalToken: string; estimate: { estimatedCostChf: number }; provider: string; model: string };
type Plan = { commands: string[]; targetVersion?: number | null; rationale: string; estimate: unknown; planId: string };
export default function StudioAiTranscription({ dossierId, phrase, connected, allowed, catalog, onPlan, onSearch }: {
  dossierId: string; phrase: string; connected: boolean; allowed: boolean; catalog: StudioCommand[];
  onSearch?: () => void;
  onPlan: (commands: StudioCommand[], targetVersion: number | undefined, planId: string, rationale: string) => void;
}) {
  const params = useParams();
  const intl = useIntl(); const prefix = ['moduleStudio', 'command', 'ai'].join('.'); const t = (id: string) => intl.formatMessage({ id: prefix + '.' + id });
  const [preparation, setPreparation] = useState<Preparation | null>(null);
  const [busy, setBusy] = useState(false), [failed, setFailed] = useState(false);
  const scope = useRef(0);
  useEffect(() => { const token = ++scope.current; setPreparation(null); setFailed(false); setBusy(false); return () => { scope.current = token + 1; }; }, [dossierId, phrase]);
  const request = async (approvalToken?: string) => {
    if (busy || !phrase || phrase.length > 512) return;
    const token = scope.current; setBusy(true); setFailed(false);
    try {
      const response = await BaseApi.post('/api/v1/module-studio/dossiers/' + encodeURIComponent(dossierId) + '/commands/transcribe', { phrase, ...(approvalToken ? { approvalToken } : {}) });
      if (token !== scope.current) return;
      if (!approvalToken) {
        const result = response.data as Preparation;
        if (!result.available || !result.approvalToken || typeof result.estimate?.estimatedCostChf !== 'number') throw new Error('studio.command.no_estimate');
        setPreparation(result);
      } else {
        const result = response.data as Plan;
        if (!result.commands?.length || !result.planId || result.commands.some(key => !catalog.some(command => command.key === key))) throw new Error('studio.command.transcription_invalid');
        onPlan(result.commands.map(key => catalog.find(command => command.key === key)!), result.targetVersion ?? undefined, result.planId, result.rationale);
      }
    } catch { if (token === scope.current) { setFailed(true); setPreparation(null); } }
    finally { if (token === scope.current) setBusy(false); }
  };
  return <section>
    {!connected || !allowed ? <p>{t(!connected ? 'connectionRequired' : 'permissionRequired')} <a href={'/adminportal/' + encodeURIComponent(params.workspaceId ?? 'default') + '/ai/provider-settings'}>{t('registry')}</a></p> : <>
      <p>{t('privacy')}</p>
      {!preparation ? <Button disabled={busy || !phrase || phrase.length > 512} onClick={() => void request()}>{t('ask')}</Button> : <>
        <p>{intl.formatMessage({ id: 'moduleStudio.command.cost' }, { provider: preparation.provider, model: preparation.model, cost: intl.formatNumber(preparation.estimate.estimatedCostChf, { style: 'currency', currency: 'CHF', minimumFractionDigits: 4, maximumFractionDigits: 4 }) })}</p>
        <Button disabled={busy} onClick={() => void request(preparation.approvalToken)}>{t('approve')}</Button>
      </>}
    </>}
    {failed ? <><p role="alert">{t('fallback')}</p>{onSearch ? <Button onClick={onSearch}>{t('search')}</Button> : null}</> : null}
  </section>;
}
